/**
 * Admin layout scaffold.
 *
 * Shared sidebar + main-content wrapper used by every admin page.
 * On mobile (≤768px), sidebar becomes a flyout triggered by a
 * hamburger button in the top-left.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import './AdminLayout.css';


const NAV_SECTIONS = [
  {
    label: 'people',
    items: [
      { to: '/admin/users',     name: 'Users' },
      { to: '/admin/invites',   name: 'Invites' },
      { to: '/admin/requests',  name: 'Requests' },
    ],
  },
  {
    label: 'content',
    items: [
      { to: '/admin/tracks',      name: 'Tracks' },
      { to: '/admin/quotes',      name: 'Quotes' },
      { to: '/admin/comics',      name: 'Comics' },
      { to: '/admin/suggestions', name: 'Suggestions' },
      { to: '/admin/comments',    name: 'Comments' },
    ],
  },
  {
    label: 'system',
    items: [
      { to: '/admin/reactions', name: 'Reactions' },
      { to: '/admin/settings',  name: 'Settings' },
    ],
  },
];


export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (user tapped a nav item)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar overlay is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  if (!user) return null;

  const initials = user.display_name.slice(0, 2).toUpperCase();

  return (
    <div className="admin-shell">
      {/* Mobile hamburger — only visible on small screens via CSS */}
      <button
        className="admin-mobile-toggle"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open menu"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <rect y="3" width="20" height="2" rx="1" />
          <rect y="9" width="20" height="2" rx="1" />
          <rect y="15" width="20" height="2" rx="1" />
        </svg>
      </button>

      {/* Backdrop — visible only when sidebar is open on mobile */}
      {sidebarOpen && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-top">
          <a href="/" className="admin-brand" title="back to home">
            <img
              className="admin-brand-img"
              src="/nachofiles-wordmark-small.png"
              alt="NachoFiles"
            />
            <span className="admin-badge">admin</span>
          </a>

          {/* Close button — only visible on mobile */}
          <button
            className="admin-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        {NAV_SECTIONS.map((section) => (
          <div key={section.label} className="admin-nav-group">
            <div className="admin-nav-label">{section.label}</div>
            <nav className="admin-nav">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? 'admin-nav-item active' : 'admin-nav-item'
                  }
                >
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        ))}

        <div className="admin-user">
        <div className="admin-user-avatar">
        {user.avatar_url
          ? <img src={`${user.avatar_url}?v=${Date.now()}`} alt="" className="mnav-avatar-img" />
          : initials
        }
        </div>
          <div className="admin-user-info">
            <div className="admin-user-name">{user.display_name}</div>
            <div className="admin-user-meta">{user.role}</div>
          </div>
          <button
            className="admin-user-exit"
            onClick={handleLogout}
            title="Sign out"
          >
            ↗
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
